import { Link, useLocation } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import * as api from '../../api/client';
import { useResponseReport } from '../../hooks/useReports';
import { useReportParams } from './ScopeRangePicker';
import { Card } from '../common/Card';
import { Skeleton } from '../common/Skeleton';
import { useChartTheme } from '../../utils/chartTheme';
import { CoverageNote } from './CoverageNote';
import { EstimateBadge } from './EstimateBadge';

/** Every metric here is nullable. Null renders as an em dash, never as 0 or 100. */
const num = (v: number | null | undefined, digits = 0, suffix = '') =>
  v === null || v === undefined ? '—' : `${v.toFixed(digits)}${suffix}`;

export function ResponseReport() {
  const { query } = useReportParams();
  const { data, isLoading, isError } = useResponseReport(query);
  const t = useChartTheme();
  const location = useLocation();

  if (isLoading) {
    return (
      <div className="space-y-3" aria-busy="true" aria-label="Loading report">
        {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-12 w-full" />)}
      </div>
    );
  }
  if (isError || !data) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">Could not load this report.</Card>;
  }
  if (data.rows.length === 0) {
    return <Card className="p-10 text-center text-gray-500 dark:text-gray-400">No services match this scope.</Card>;
  }

  const meanApprox = data.meta.source.response_mean === 'hourly-approx';
  const percentileEstimate = data.meta.source.percentiles === 'hourly-estimate';

  // Slowest first — that is the point of this table.
  const sorted = [...data.rows].sort((a, b) => (b.p95 ?? -1) - (a.p95 ?? -1));

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <CoverageNote coverage={data.meta.coverage} />
        <a
          href={api.reportCsvUrl('response', query)}
          download
          className="no-print text-sm text-brand-600 dark:text-brand-400 hover:underline"
        >
          Export CSV
        </a>
      </div>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th scope="col" className="text-left px-4 py-3">Service</th>
              <th scope="col" className="text-right px-2 py-3">
                Mean{meanApprox && <EstimateBadge kind="mean-approx" />}
              </th>
              <th scope="col" className="text-right px-2 py-3">
                p50
              </th>
              <th scope="col" className="text-right px-2 py-3">
                p95{percentileEstimate && <EstimateBadge kind="percentile-estimate" />}
              </th>
              <th scope="col" className="text-right px-2 py-3">
                p99
              </th>
              <th scope="col" className="text-right px-2 py-3">
                Worst hour p95
              </th>
              <th scope="col" className="text-right px-2 py-3">Min</th>
              <th scope="col" className="text-right px-4 py-3">Max</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {sorted.map(r => (
              <tr key={r.service_id} className="hover:bg-gray-50 dark:hover:bg-gray-700/40">
                <td className="px-4 py-2.5">
                  <Link
                    to={{ pathname: `/reports/services/${r.service_id}`, search: location.search }}
                    className="text-brand-600 dark:text-brand-400 hover:underline"
                  >
                    {r.service_name}
                  </Link>
                </td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.mean_response, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.p50, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.p95, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.p99, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.worst_hour_p95, 0, ' ms')}</td>
                <td className="px-2 py-2.5 text-right font-mono tabular-nums">{num(r.min_response, 0, ' ms')}</td>
                <td className="px-4 py-2.5 text-right font-mono tabular-nums">{num(r.max_response, 0, ' ms')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Card className="p-4">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Mean response by hour</h3>
        {data.hour_of_day.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
            No response-time data in this period.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={data.hour_of_day} margin={{ top: 8, right: 8, bottom: 24, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke={t.grid} vertical={false} />
              <XAxis
                dataKey="hour_of_day"
                tick={{ fontSize: 11, fill: t.axis }}
                stroke={t.grid}
                label={{ value: 'Hour of day (UTC)', position: 'insideBottom', offset: -16, fill: t.axis, fontSize: 12 }}
              />
              <YAxis tick={{ fontSize: 11, fill: t.axis }} stroke={t.grid} unit=" ms" width={56} />
              <Tooltip
                contentStyle={t.tooltip}
                labelStyle={{ color: t.axis }}
                labelFormatter={(v: string) => `Hour ${v}:00 UTC`}
                formatter={(value: unknown) => [value === null || value === undefined ? '—' : `${value} ms`, 'Mean response']}
              />
              <Bar dataKey="mean_response" fill={t.bar} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </Card>
    </div>
  );
}
